import { useCallback, useEffect, useRef, useState } from 'react';
import type { ImageData } from '../components/common/PropertyImageUpload';
import { getCookie } from '../components/utils/utils';

export interface ListingImageData extends ImageData {
  isEnhancing?: boolean;
}

export interface EnhancementStatus {
  enabled: boolean;
  credits: number;
  price_per_package: number;
  credits_per_package: number;
}

interface UseListingImageUploadOptions {
  maxImages?: number;
  onCreditsRequired?: () => void | Promise<void>;
}

const MAX_FILE_SIZE = 15 * 1024 * 1024;
const DEFAULT_MAX_IMAGES = 30;
const MAX_CONCURRENT_UPLOADS = 3;
/** Tile error for a file the server refused (400) as an unsupported or damaged image, so the tile can say why (JAA-433). */
export const UNSUPPORTED_IMAGE_ERROR = 'UNSUPPORTED_IMAGE';
const HEIC_PLACEHOLDER = 'data:image/svg+xml;charset=UTF-8,' + encodeURIComponent(`
  <svg xmlns="http://www.w3.org/2000/svg" width="320" height="240" viewBox="0 0 320 240">
    <defs>
      <linearGradient id="g" x1="0" y1="0" x2="1" y2="1">
        <stop offset="0%" stop-color="#EAE7FF"/>
        <stop offset="100%" stop-color="#D9F0FF"/>
      </linearGradient>
    </defs>
    <rect rx="16" ry="16" width="100%" height="100%" fill="url(#g)"/>
    <text x="160" y="128" text-anchor="middle" font-family="Inter,system-ui,Segoe UI,Roboto,Arial" font-size="16" fill="#4B5563">Loading photo...</text>
  </svg>
`);

const isHeifFile = (file: File) => {
  const lowerName = (file.name || '').toLowerCase();
  return lowerName.endsWith('.heic')
    || lowerName.endsWith('.heif')
    || file.type === 'image/heic'
    || file.type === 'image/heif';
};

const isImageFile = (file: File) => (file.type || '').startsWith('image/') || isHeifFile(file);

export const buildImagePayload = (
  data: Record<string, any>,
  fallbackId: string,
): ListingImageData => {
  const originalUrl = data.originalUrl || data.url;
  const originalThumbnailUrl = data.originalThumbnailUrl || data.thumbnailUrl || originalUrl;

  return {
    id: data.id || fallbackId,
    // AMP defaults to the unenhanced image. The old lightbox toggle changes
    // these active URL fields, and those active fields are persisted on publish.
    url: originalUrl,
    thumbnailUrl: originalThumbnailUrl,
    uploadedAt: data.uploadedAt,
    uploadProgress: 100,
    isUploading: false,
    isEnhancing: false,
    originalUrl,
    enhancedUrl: data.enhancedUrl,
    originalThumbnailUrl,
    enhancedThumbnailUrl: data.enhancedThumbnailUrl,
    enhancementFailed: data.enhancementFailed,
    isDisplayOptimized: data.isDisplayOptimized === true,
  };
};

const fetchWithRetry = async (
  endpoint: string,
  formData: FormData,
  attempts = 3,
  retryStatuses = [500, 502, 503, 504],
): Promise<Response> => {
  let lastError: unknown = null;

  for (let attempt = 0; attempt < attempts; attempt += 1) {
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}${endpoint}`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'X-CSRF-TOKEN': getCookie('csrf_access_token') || '',
        },
        body: formData,
      });

      if (!retryStatuses.includes(response.status)) {
        return response;
      }

      lastError = new Error(`Server error ${response.status}`);
    } catch (error) {
      lastError = error;
    }

    await new Promise((resolve) => setTimeout(resolve, 300 * Math.pow(2, attempt)));
  }

  throw lastError || new Error('Upload failed');
};

export const useListingImageUpload = ({
  maxImages = DEFAULT_MAX_IMAGES,
  onCreditsRequired,
}: UseListingImageUploadOptions = {}) => {
  const [images, setImages] = useState<ListingImageData[]>([]);
  const [uploadError, setUploadError] = useState('');
  const [isRegenerating, setIsRegenerating] = useState(false);
  const [enhancementStatus, setEnhancementStatus] = useState<EnhancementStatus | null>(null);
  const objectUrlsRef = useRef<Set<string>>(new Set());

  const fetchEnhancementStatus = useCallback(async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/ai/enhancement-status`, {
        method: 'GET',
        credentials: 'include',
        headers: {
          'X-CSRF-TOKEN': getCookie('csrf_access_token') || '',
        },
      });

      if (response.ok) {
        const data = await response.json();
        setEnhancementStatus(data);
      }
    } catch (error) {
      console.error('Error fetching enhancement status:', error);
    }
  }, []);

  const decrementCredits = useCallback((amount = 1) => {
    setEnhancementStatus((prev) => (
      prev ? { ...prev, credits: Math.max(0, prev.credits - amount) } : prev
    ));
  }, []);

  const revokeObjectUrl = useCallback((url?: string) => {
    if (!url || !url.startsWith('blob:')) return;
    URL.revokeObjectURL(url);
    objectUrlsRef.current.delete(url);
  }, []);

  const resetImages = useCallback(() => {
    objectUrlsRef.current.forEach((url) => URL.revokeObjectURL(url));
    objectUrlsRef.current.clear();
    setImages([]);
    setUploadError('');
    setIsRegenerating(false);
  }, []);

  useEffect(() => resetImages, [resetImages]);

  const restoreImages = useCallback((restoredImages: ListingImageData[]) => {
    objectUrlsRef.current.forEach((url) => URL.revokeObjectURL(url));
    objectUrlsRef.current.clear();
    setImages(restoredImages.map((image) => ({
      ...image,
      isUploading: false,
      isEnhancing: false,
      uploadProgress: image.uploadProgress ?? 100,
      error: undefined,
    })));
    setUploadError('');
    setIsRegenerating(false);
  }, []);

  /**
   * Uploads one photo through the AI route and falls back to the plain route on 402/503. A 400 becomes
   * UNSUPPORTED_IMAGE_ERROR so the tile can say the format was refused instead of a generic failure.
   */
  const uploadSingleFile = useCallback(async (file: File, onEnhanceFallback?: () => void) => {
    const enhanceFormData = new FormData();
    enhanceFormData.append('image', file);
    enhanceFormData.append('enhance', 'true');
    enhanceFormData.append('style', 'nordic');

    let response = await fetchWithRetry(
      '/ai/enhance-and-upload-image',
      enhanceFormData,
      3,
      [500, 502, 504],
    );

    if (response.status === 402 || response.status === 503) {
      // Out of credits / enhancement disabled: fall back to a plain upload. Let the
      // caller drop the "Enhancing" state so the tile shows plain "Uploading".
      onEnhanceFallback?.();
      const plainFormData = new FormData();
      plainFormData.append('image', file);
      response = await fetchWithRetry('/upload-property-image', plainFormData, 3);
    }

    if (response.status === 400) {
      throw new Error(UNSUPPORTED_IMAGE_ERROR);
    }

    if (!response.ok) {
      throw new Error(`Upload failed with status ${response.status}`);
    }

    return response.json();
  }, []);

  const uploadFiles = useCallback(async (files: FileList | File[]) => {
    const fileArray = Array.from(files);
    const imageFiles = fileArray.filter(isImageFile);

    if (imageFiles.length === 0) return;

    if (images.length + imageFiles.length > maxImages) {
      setUploadError(`You can add up to ${maxImages} photos.`);
      return;
    }

    const oversizedFile = imageFiles.find((file) => file.size > MAX_FILE_SIZE);
    if (oversizedFile) {
      setUploadError('Image is too large. Maximum size is 15MB.');
      return;
    }

    // Only show the "Enhancing" state when enhancement will actually run. When the
    // status is known and the user is out of credits (or enhancement is disabled),
    // the upload falls back to a plain upload, so show plain "Uploading" instead.
    const willEnhance = enhancementStatus
      ? Boolean(enhancementStatus.enabled) && enhancementStatus.credits > 0
      : true;

    const optimisticImages = imageFiles.map((file, index) => {
      const objectUrl = isHeifFile(file) ? HEIC_PLACEHOLDER : URL.createObjectURL(file);
      if (objectUrl.startsWith('blob:')) objectUrlsRef.current.add(objectUrl);

      return {
        id: `upload-${Date.now()}-${index}-${file.name}`,
        url: objectUrl,
        thumbnailUrl: objectUrl,
        isUploading: true,
        isEnhancing: willEnhance,
        uploadProgress: 0,
        error: undefined,
      } as ListingImageData;
    });

    setImages((prev) => [...prev, ...optimisticImages]);
    setUploadError('');

    const uploadAndCommit = async (file: File, optimisticImage: ListingImageData) => {
      try {
        const data = await uploadSingleFile(file, () => {
          setImages((prev) => prev.map((image) => (
            image.id === optimisticImage.id ? { ...image, isEnhancing: false } : image
          )));
        });
        const uploadedImage = buildImagePayload(data, optimisticImage.id);

        setImages((prev) => prev.map((image) => (
          image.id === optimisticImage.id ? uploadedImage : image
        )));

        if (data.enhancedUrl) {
          decrementCredits();
        }
      } catch (error) {
        setImages((prev) => prev.map((image) => (
          image.id === optimisticImage.id
            ? {
              ...image,
              isUploading: false,
              isEnhancing: false,
              error: error instanceof Error ? error.message : String(error),
            }
            : image
        )));
      } finally {
        revokeObjectUrl(optimisticImage.url);
      }
    };

    const uploadQueue = imageFiles.map((file, index) => ({
      file,
      optimisticImage: optimisticImages[index],
    }));

    for (let start = 0; start < uploadQueue.length; start += MAX_CONCURRENT_UPLOADS) {
      const batch = uploadQueue.slice(start, start + MAX_CONCURRENT_UPLOADS);
      // eslint-disable-next-line no-await-in-loop
      await Promise.all(batch.map(({ file, optimisticImage }) => uploadAndCommit(file, optimisticImage)));
    }
  }, [images.length, maxImages, revokeObjectUrl, uploadSingleFile, decrementCredits, enhancementStatus]);

  const removeImage = useCallback((index: number) => {
    setImages((prev) => {
      const image = prev[index];
      if (image) {
        revokeObjectUrl(image.url);
        revokeObjectUrl(image.thumbnailUrl);
      }
      return prev.filter((_, imageIndex) => imageIndex !== index);
    });
  }, [revokeObjectUrl]);

  const reorderImages = useCallback((fromIndex: number, toIndex: number) => {
    setImages((prev) => {
      if (fromIndex === toIndex || fromIndex < 0 || toIndex < 0) return prev;
      if (fromIndex >= prev.length || toIndex >= prev.length) return prev;

      const next = [...prev];
      const [moved] = next.splice(fromIndex, 1);
      next.splice(toIndex, 0, moved);
      return next;
    });
  }, []);

  const toggleImageVersion = useCallback((index: number) => {
    setImages((prev) => {
      const next = [...prev];
      const image = next[index];

      if (!image?.enhancedUrl || !image.originalUrl) return prev;

      const showEnhanced = image.url !== image.enhancedUrl;
      next[index] = {
        ...image,
        url: showEnhanced ? image.enhancedUrl : image.originalUrl,
        thumbnailUrl: showEnhanced
          ? (image.enhancedThumbnailUrl || image.enhancedUrl)
          : (image.originalThumbnailUrl || image.originalUrl),
      };

      return next;
    });
  }, []);

  const regenerateImage = useCallback(async (index: number) => {
    const image = images[index];
    if (!image?.id || isRegenerating) return;

    setIsRegenerating(true);
    setUploadError('');
    setImages((prev) => prev.map((item) => (
      item.id === image.id ? { ...item, isEnhancing: true } : item
    )));

    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/ai/enhance-and-upload-image`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-TOKEN': getCookie('csrf_access_token') || '',
        },
        body: JSON.stringify({ imageId: image.id, style: 'nordic' }),
      });

      if (response.status === 402) {
        setImages((prev) => prev.map((item) => (
          item.id === image.id ? { ...item, isEnhancing: false } : item
        )));
        await onCreditsRequired?.();
        return;
      }

      if (!response.ok) {
        throw new Error(`Re-enhance failed with status ${response.status}`);
      }

      const data = await response.json();
      const nextEnhancedUrl = data.enhancedUrl;
      const nextEnhancedThumbnailUrl = data.enhancedThumbnailUrl || data.enhancedUrl;

      setImages((prev) => prev.map((item) => (
        item.id === image.id
          ? {
            ...item,
            isEnhancing: false,
            enhancedUrl: nextEnhancedUrl,
            enhancedThumbnailUrl: nextEnhancedThumbnailUrl,
            url: nextEnhancedUrl,
            thumbnailUrl: nextEnhancedThumbnailUrl,
            enhancementFailed: false,
          }
          : item
      )));

      decrementCredits();
    } catch (error) {
      console.error('Failed to regenerate image:', error);
      setUploadError(error instanceof Error ? error.message : String(error));
      setImages((prev) => prev.map((item) => (
        item.id === image.id ? { ...item, isEnhancing: false } : item
      )));
    } finally {
      setIsRegenerating(false);
    }
  }, [images, isRegenerating, onCreditsRequired, decrementCredits]);

  const isUploading = images.some((image) => image.isUploading);

  return {
    images,
    uploadError,
    isUploading,
    isRegenerating,
    enhancementStatus,
    fetchEnhancementStatus,
    uploadFiles,
    removeImage,
    reorderImages,
    toggleImageVersion,
    regenerateImage,
    resetImages,
    restoreImages,
  };
};

export default useListingImageUpload;
